import { useTranslation } from "react-i18next";
import {
  HiOutlineRefresh,
  HiOutlineCollection,
  HiOutlineInformationCircle,
} from "react-icons/hi";
import { cardStat } from "@design-system";
import type { OrderTemplate } from "../../models";
import { Timestamp } from "../../types/Timestamp";
import { describeEnd, describeSchedule } from "./recurrenceSummary";

/** A schedule and its boundaries; template dates are calendar dates, not execution timestamps. */
export const RecurrenceOverview = ({
  template,
}: {
  template: OrderTemplate;
}) => {
  const { t, i18n } = useTranslation(["orders", "common"]);
  const rule = template.recurrence;
  if (!rule) return null;
  const locale =
    i18n.language.startsWith("no") || i18n.language.startsWith("nb")
      ? "nb-NO"
      : "en-GB";
  const dateCard = (label: string, value?: string, fallback?: string) => {
    const date = Timestamp.toMoment(value);
    const valid = date.isValid();
    const jsDate = date.toDate();
    return (
      <div
        className={`${cardStat} min-w-0 flex flex-col items-start sm:flex-row sm:items-center gap-3`}
      >
        <div
          aria-hidden="true"
          className="w-14 shrink-0 overflow-hidden rounded-md border border-border bg-surface text-center"
        >
          {valid ? (
            <>
              <div className="bg-brand-muted py-1 text-xs font-medium text-brand uppercase">
                {new Intl.DateTimeFormat(locale, { month: "short" }).format(
                  jsDate,
                )}
              </div>
              <div className="pt-1 text-2xl font-semibold text-text leading-tight">
                {date.date()}
              </div>
              <div className="pb-1 text-xs text-secondary">{date.year()}</div>
            </>
          ) : (
            <div className="flex h-20 items-center justify-center text-brand">
              {rule.endMode === "afterCount" ? (
                <HiOutlineCollection className="h-6 w-6" />
              ) : (
                <HiOutlineRefresh className="h-6 w-6" />
              )}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-text">{label}</div>
          {valid ? (
            <time
              dateTime={value}
              className="mt-1 block text-sm text-secondary"
            >
              {new Intl.DateTimeFormat(locale, {
                day: "numeric",
                month: "long",
                year: "numeric",
              }).format(jsDate)}
            </time>
          ) : (
            <p className="mt-1 text-sm text-secondary">{fallback || "—"}</p>
          )}
        </div>
      </div>
    );
  };
  const history = [
    ...(rule.active && template.generationState?.nextOccurrence
      ? [
          {
            label: t("templates.details.next_run"),
            value: Timestamp.toDisplayDate(
              template.generationState.nextOccurrence,
            ),
          },
        ]
      : []),
    ...(template.generationState?.lastOccurrence
      ? [
          {
            label: t("templates.details.last_run"),
            value: Timestamp.toDisplayDate(
              template.generationState.lastOccurrence,
            ),
          },
        ]
      : []),
    ...(template.generationState?.generatedCount
      ? [
          {
            label: t("templates.details.generated_count"),
            value: template.generationState.generatedCount,
          },
        ]
      : []),
  ];
  return (
    <div>
      <div className={`${cardStat} flex items-center gap-3`}>
        <div
          className="rounded-lg bg-surface-muted p-2.5 text-secondary"
          aria-hidden="true"
        >
          <HiOutlineRefresh className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-sm text-secondary">
            {t("templates.details.schedule")}
          </div>
          <p className="mt-0.5 font-medium text-text">
            {describeSchedule(t, template)}
          </p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {dateCard(t("templates.details.starts"), rule.startDate)}
        {dateCard(
          t("templates.details.ends"),
          rule.endMode === "onDate" ? rule.endDate : undefined,
          describeEnd(t, template),
        )}
      </div>
      <p className="mt-3 flex items-start gap-1.5 text-xs text-secondary">
        <HiOutlineInformationCircle
          className="mt-px h-4 w-4 shrink-0"
          aria-hidden="true"
        />
        {t("templates.summary.lead", { count: rule.leadTimeDays ?? 0 })}
      </p>
      {history.length > 0 && (
        <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-border pt-3 text-xs text-secondary">
          {history.map(({ label, value }) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd className="mt-0.5 font-medium text-text">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
